import * as React from 'react'
import { FileInfoModal } from '@/modules/imports/ui/FileInfoModal'
import type { FileDetailsDto } from '@/lib/api/company/fileProcessing/fileDetails/types'
import { FileType } from '@/app/constants/fileType'
import { BasicFileInfoModal } from './BasicFileInfoModal'
import { HistoryRow } from './types'

type Props = {
  companyId: string
  row: HistoryRow
  onClose: () => void
  onDownload: (id: string) => void
}

function toIso(v: unknown): string | null {
  if (!v) return null
  if (typeof v === 'string') return v
  if (typeof (v as any).toISOString === 'function') return (v as any).toISOString()
  return String(v)
}

function buildDetailsFromJson(row: HistoryRow, json: string): FileDetailsDto | null {
  let raw: any
  try {
    raw = JSON.parse(json)
  } catch {
    return null
  }
  const cd = raw?.cardDetails ?? {}
  const vr = raw?.verifyReport ?? {}
  return {
    source: row.source,
    readStatus: row.readStatus,
    certificateStatus: row.certificateStatus,
    cardValidFrom: toIso(row.raw.cardValidFrom),
    cardExpirationDate: toIso(row.raw.cardExpirationDate),
    firstActivityTime: toIso(row.raw.firstActivityTime),
    lastActivityTime: toIso(row.raw.lastActivityTime),
    downloadTime: toIso(row.raw.downloadTime),
    fileName: row.fileName,
    error: null,
    isCertificateValidityDateMismatch: vr.isCertificateValidityDateMismatch ?? false,
    certificateValidityDate: vr.certificateValidityDate ?? null,
    tachographDetails: cd.tachographDetails ?? null,
    companyLocks: cd.companyLocks ?? [],
    calibrations: cd.calibrationDetails ?? [],
    faults: cd.faultDetails ?? [],
    controls: cd.controlDetails ?? [],
    signatures: (vr.signatureInfos ?? []).map((s: any) => ({
      signatureBlockTypeName: s.signatureBlockTypeName ?? null,
      signatureCorrect: s.signatureCorrect ?? true,
      activityDateFormatted: s.activityDateFormatted ?? null,
    })),
    fileStructureWarnings: raw?.fileWarnings ?? [],
    speedInfo: cd.vehicleSpeedInformation
      ? {
          vehicleRegistrationNumber: cd.vehicleSpeedInformation.vehicleRegistrationNumber ?? null,
          speedPerMinute: (cd.vehicleSpeedInformation.speedPerMinute ?? []).map((p: any) => ({
            minute: p.minute,
            speed: p.speed ?? [],
          })),
        }
      : null,
  }
}

function buildBasicDddDetails(row: HistoryRow): FileDetailsDto {
  return {
    source: row.source,
    readStatus: row.readStatus,
    certificateStatus: row.certificateStatus,
    cardValidFrom: toIso(row.raw.cardValidFrom),
    cardExpirationDate: toIso(row.raw.cardExpirationDate),
    firstActivityTime: toIso(row.raw.firstActivityTime),
    lastActivityTime: toIso(row.raw.lastActivityTime),
    downloadTime: toIso(row.raw.downloadTime),
    fileName: row.fileName,
    error: null,
    isCertificateValidityDateMismatch: false,
    certificateValidityDate: null,
    tachographDetails: null,
    companyLocks: [],
    calibrations: [],
    faults: [],
    controls: [],
    signatures: [],
    fileStructureWarnings: [],
    speedInfo: null,
  }
}

export const FileDetailsDialog: React.FC<Props> = ({ row, onClose }) => {
  // Driver-card / vehicle DDD files → rich tachograph modal with signatures, calibrations, etc.
  // Anything else (xlsx, csv, gps, tms) → simple modal showing fields that actually apply to
  // tabular imports (read status, import status, valid/invalid line counts, logs).
  if (row.fileType === FileType.Ddd) {
    const details =
      (row.cardReportDetailsJson && buildDetailsFromJson(row, row.cardReportDetailsJson)) ||
      buildBasicDddDetails(row)
    return <FileInfoModal fileName={row.fileName} details={details} onClose={onClose} />
  }
  return <BasicFileInfoModal row={row} onClose={onClose} />
}
